/**
 * SettingsPage Component
 * User settings management page — renders dynamically from backend setting type definitions
 */

import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  Cog6ToothIcon,
  SunIcon,
  MoonIcon,
  BellIcon,
  TableCellsIcon,
  ClockIcon,
  GlobeAltIcon,
  ArrowPathIcon,
  Squares2X2Icon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathButton,
  OathDualSpinner,
  OathAlert,
} from '../../shared/OathUI';
import { useOathSettings } from '../../../services/oathSettingsContext';

// ============================================
// Category icon mapping
// ============================================

const CATEGORY_ICONS = {
  APPEARANCE: SunIcon,
  DISPLAY: SunIcon,
  NOTIFICATIONS: BellIcon,
  DASHBOARD: Squares2X2Icon,
  TABLES: TableCellsIcon,
  TABLE: TableCellsIcon,
  LOCALIZATION: GlobeAltIcon,
  SESSION: ClockIcon,
};

const getCategoryIcon = (category, isDarkMode) => {
  if ((category === 'APPEARANCE' || category === 'DISPLAY') && isDarkMode) return MoonIcon;
  return CATEGORY_ICONS[category] || AdjustmentsHorizontalIcon;
};

// ============================================
// Shared setting controls
// ============================================

const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed ${
      checked ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-600'
    }`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

const SettingRow = ({ label, description, children }) => (
  <div className="flex items-center justify-between py-4 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <div className="flex-1 min-w-0 pr-4">
      <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
      {description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
      )}
    </div>
    <div className="flex-shrink-0">{children}</div>
  </div>
);

const SettingSelect = ({ value, onChange, options, disabled = false }) => (
  <select
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className="block w-40 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {options.map((opt) => (
      <option key={opt.value} value={opt.value}>
        {opt.label}
      </option>
    ))}
  </select>
);

const SettingInput = ({ value, onChange, disabled = false, type = 'text' }) => (
  <input
    type={type}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className="block w-40 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
  />
);

const SettingsSection = ({ icon: Icon, title, children }) => (
  <div className="mb-6 last:mb-0">
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="h-5 w-5 text-primary dark:text-primary-light" />}
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
        {title}
      </h3>
    </div>
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl px-4">{children}</div>
  </div>
);

// ============================================
// Dynamic setting control renderer
// ============================================

const SettingControl = ({ settingType, value, onUpdate, isSaving }) => {
  const { settingKey, dataType, defaultValue } = settingType;
  const disabled = isSaving;
  const effectiveValue = value ?? defaultValue;

  if (dataType === 'boolean') {
    return (
      <ToggleSwitch
        checked={effectiveValue === 'true' || effectiveValue === true}
        onChange={(val) => onUpdate(settingKey, val)}
        disabled={disabled}
      />
    );
  }

  if (dataType === 'integer') {
    return (
      <SettingInput
        type="number"
        value={effectiveValue}
        onChange={(val) => onUpdate(settingKey, val)}
        disabled={disabled}
      />
    );
  }

  // Default: string — render as text input
  return (
    <SettingInput
      value={effectiveValue}
      onChange={(val) => onUpdate(settingKey, val)}
      disabled={disabled}
    />
  );
};

// ============================================
// Main settings page
// ============================================

const SettingsPage = () => {
  const {
    isLoading,
    isDarkMode,
    settingTypesByCategory,
    updateSetting,
    resetSettings,
    getSettingValue,
  } = useOathSettings();

  const [savingKey, setSavingKey] = useState(null);
  const [isResetting, setIsResetting] = useState(false);

  const handleUpdateSetting = async (key, value) => {
    setSavingKey(key);
    const result = await updateSetting(key, value);
    setSavingKey(null);

    if (result.success) {
      toast.success('Setting updated');
    } else {
      toast.error(result.error || 'Failed to update setting');
    }
  };

  const handleResetSettings = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to their defaults?')) {
      return;
    }

    setIsResetting(true);
    const result = await resetSettings();
    setIsResetting(false);

    if (result.success) {
      toast.success('Settings reset to defaults');
    } else {
      toast.error(result.error || 'Failed to reset settings');
    }
  };

  // Ordered list of categories (preserve backend display order via first item in each group)
  const orderedCategories = useMemo(() => {
    const entries = Object.entries(settingTypesByCategory);
    // Sort categories by the minimum displayOrder within each
    entries.sort((a, b) => {
      const minA = Math.min(...a[1].map((t) => t.displayOrder || 0));
      const minB = Math.min(...b[1].map((t) => t.displayOrder || 0));
      return minA - minB;
    });
    return entries;
  }, [settingTypesByCategory]);

  if (isLoading) {
    return (
      <OathPageWrapper>
        <div className="flex flex-col items-center justify-center py-20">
          <OathDualSpinner size="lg" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading settings...</p>
        </div>
      </OathPageWrapper>
    );
  }

  const hasSettings = orderedCategories.length > 0;

  return (
    <OathPageWrapper>
      <OathHeaderCard
        title="Settings"
        subtitle="Customize your experience"
        icon={Cog6ToothIcon}
        rightContent={
          <OathButton
            variant="outline"
            size="sm"
            onClick={handleResetSettings}
            loading={isResetting}
            icon={ArrowPathIcon}
          >
            Reset All
          </OathButton>
        }
      />

      {!hasSettings ? (
        <OathContentCard>
          <OathAlert variant="warning" message="No settings available. Please try again later." />
        </OathContentCard>
      ) : (
        <OathContentCard>
          {orderedCategories.map(([category, types]) => {
            const Icon = getCategoryIcon(category, isDarkMode);
            const categoryLabel = category.charAt(0) + category.slice(1).toLowerCase();

            return (
              <SettingsSection key={category} icon={Icon} title={categoryLabel}>
                {types.map((type) => (
                  <SettingRow
                    key={type.settingKey}
                    label={type.settingName}
                    description={type.settingDescription}
                  >
                    <SettingControl
                      settingType={type}
                      value={getSettingValue(type.settingKey)}
                      onUpdate={handleUpdateSetting}
                      isSaving={savingKey === type.settingKey}
                    />
                  </SettingRow>
                ))}
              </SettingsSection>
            );
          })}
        </OathContentCard>
      )}
    </OathPageWrapper>
  );
};

export default SettingsPage;
